import { plural, t } from '@lingui/macro';
import { TireQuoteItem } from 'noddi-types';
import { ErrorPage, NoddiBasicCard, NoddiButton } from 'noddi-ui-web';
import { customerRoutes, formatCurrencyAmount, getTirePrice } from 'noddi-util';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import getCommonTranslations from '../../commonTranslations';
import { useTireOfferStore } from '../../stores/tireOfferStore';
import { getQuantityOptionsWithTrans } from '../../utils/translation';
import { StaggeredTirePositionSection, TirePosition } from './StaggeredTirePositionSection';

interface StaggeredTireSelectionCardProps {
  quoteItem: TireQuoteItem;
  slug: string;
}

export const StaggeredTireSelectionCard = ({ quoteItem, slug }: StaggeredTireSelectionCardProps) => {
  const { setSelectedQuoteItem, selectedQuoteItem } = useTireOfferStore();
  const navigate = useNavigate();

  const currentQuoteItem = quoteItem.id === selectedQuoteItem?.id ? selectedQuoteItem : quoteItem;

  const frontTire = currentQuoteItem.quoteItemTires.find((tire) => tire.staggeredPosition?.value === 'FRONT');
  const rearTire = currentQuoteItem.quoteItemTires.find((tire) => tire.staggeredPosition?.value === 'REAR');

  const [frontQuantity, setFrontQuantity] = useState(frontTire?.quantitySelected || frontTire?.quantityDefault || 0);
  const [rearQuantity, setRearQuantity] = useState(rearTire?.quantitySelected || rearTire?.quantityDefault || 0);

  if (!frontTire || !rearTire) {
    return (
      <ErrorPage
        title={t`Tire not found`}
        description={getCommonTranslations().errorContactSupport}
        logInput={{
          value: `Got a tire set without staggered position - quote with slug ${slug}`,
          severity: 'error'
        }}
      />
    );
  }

  const frontQuantityOptions = getQuantityOptionsWithTrans(frontTire.quantityMax);
  const rearQuantityOptions = getQuantityOptionsWithTrans(rearTire.quantityMax);

  function updateQuantity(position: TirePosition, value: number) {
    if (position === 'FRONT') {
      setFrontQuantity(value);
    } else {
      setRearQuantity(value);
    }
  }

  const onSelectTireOption = () => {
    const updatedQuoteItem = {
      ...currentQuoteItem,
      quoteItemTires: currentQuoteItem.quoteItemTires.map((tire) => ({
        ...tire,
        quantitySelected: tire.staggeredPosition?.value === 'FRONT' ? frontQuantity : rearQuantity
      }))
    };
    setSelectedQuoteItem(updatedQuoteItem);
    navigate(customerRoutes.tireOfferSummary.getPath({ slug }));
  };

  const frontPrice = getTirePrice(frontTire);
  const rearPrice = getTirePrice(rearTire);
  const totalPrice = frontPrice.amount * frontQuantity + rearPrice.amount * rearQuantity;

  const rearTiresText = plural(rearQuantity, {
    one: 'Rear tire',
    other: 'Rear tires'
  });

  const frontTiresText = plural(frontQuantity, {
    one: 'Front tire',
    other: 'Front tires'
  });

  return (
    <NoddiBasicCard className='flex flex-col gap-4'>
      <StaggeredTirePositionSection
        tire={frontTire}
        position='FRONT'
        quantity={frontQuantity}
        quantityOptions={frontQuantityOptions}
        updateQuantity={updateQuantity}
      />

      <StaggeredTirePositionSection
        tire={rearTire}
        position='REAR'
        quantity={rearQuantity}
        quantityOptions={rearQuantityOptions}
        updateQuantity={updateQuantity}
      />

      <div className='flex flex-col items-end justify-between gap-2'>
        <div className='flex w-full flex-col'>
          <div className='flex justify-between gap-4'>
            <div className='flex gap-1'>
              <span>{frontQuantity}</span>
              <span>{frontTiresText}</span>
            </div>
            <span>
              {formatCurrencyAmount({
                money: { amount: frontPrice.amount * frontQuantity, currency: frontPrice.currency },
                decimals: 0
              })}
            </span>
          </div>
          <div className='flex justify-between gap-4'>
            <div className='flex gap-1'>
              <span>{rearQuantity}</span>
              <span>{rearTiresText}</span>
            </div>
            <span>
              {formatCurrencyAmount({
                money: { amount: rearPrice.amount * rearQuantity, currency: rearPrice.currency },
                decimals: 0
              })}
            </span>
          </div>
          <div className='flex justify-between gap-4 font-bold'>
            <span>{t`Total`}</span>
            <span>
              {formatCurrencyAmount({
                money: { amount: totalPrice, currency: frontPrice.currency },
                decimals: 0
              })}
            </span>
          </div>
        </div>
        <div className='flex w-full justify-end sm:w-[91px]'>
          <NoddiButton className='w-full' onClick={onSelectTireOption}>{t`Select`}</NoddiButton>
        </div>
      </div>
    </NoddiBasicCard>
  );
};
